import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { FaSave, FaArrowLeft, FaTimes, FaBuilding, FaUser, FaGlobe } from 'react-icons/fa';
import PageHeader from '../components/PageHeader';
import vendorService from '../services/vendorService';
import userService from '../services/userService';
import { useAuth } from '../context/AuthContext';

function VendorEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isEdit] = useState(!!id);

  const [formData, setFormData] = useState({
    name: '',
    website: '',
    social_profiles: [],
    status: 'inactive',
    owner_id: user?.id || '',
    referred_by: '',
    verified_by: '',
    verified_at: null
  });

  const [vendorOwner, setVendorOwner] = useState(null);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [ownerSuggestions, setOwnerSuggestions] = useState([]);
  const [ownerSearching, setOwnerSearching] = useState(false);
  const [showOwnerEditor, setShowOwnerEditor] = useState(false);

  const [socialProfileInput, setSocialProfileInput] = useState('');

  const fetchVendor = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await vendorService.getById(id);

      setFormData({
        name: data.name || '',
        website: data.website || '',
        social_profiles: Array.isArray(data.social_profiles) ? data.social_profiles : [],
        status: data.status || 'inactive',
        owner_id: data.owner?.id || user?.id || '',
        referred_by: data.added_by?.username || '',
        verified_by: data.verified_by?.username || '',
        verified_at: data.verified_at || null
      });
        // populate new contact/display fields when editing
        setFormData(prev => ({
          ...prev,
          phone1: data.phone1 || '',
          phone2: data.phone2 || '',
          whatsapp: data.whatsapp || '',
          logo: data.logo || '',
          cover: data.cover || '',
          location: data.location || '',
          address: data.address || '',
          verify_badge: data.verify_badge || false
        }));
        setVendorOwner(data.owner || null);
        if (data.owner) setOwnerSearch(data.owner.username + (data.owner.email ? ` (${data.owner.email})` : ''));
    } catch (err) {
      setError('Failed to load vendor');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id, user?.id]);

  useEffect(() => {
    fetchVendor();
  }, [fetchVendor]);

  // Prevent non-admin users from opening the admin create page
  useEffect(() => {
    if (!isEdit && user && user.role !== 'admin') {
      // Redirect non-admins away from the create page
      navigate('/vendors');
    }
  }, [isEdit, user, navigate]);

  // Debounced user search for owner selector
  useEffect(() => {
    const q = ownerSearch?.trim();
    if (!q || q.length < 2) {
      setOwnerSuggestions([]);
      setOwnerSearching(false);
      return;
    }

    let cancelled = false;
    setOwnerSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await userService.getAll({ q, per_page: 10 });
        if (!cancelled) {
          // API may return { items, data } or an array
          const list = Array.isArray(res) ? res : res.items || res.data || res.users || [];
          setOwnerSuggestions(list);
        }
      } catch (err) {
        console.error('User search failed', err);
        if (!cancelled) setOwnerSuggestions([]);
      } finally {
        if (!cancelled) setOwnerSearching(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [ownerSearch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddSocialProfile = () => {
    if (socialProfileInput.trim() && !formData.social_profiles.includes(socialProfileInput.trim())) {
      setFormData({
        ...formData,
        social_profiles: [...formData.social_profiles, socialProfileInput.trim()]
      });
      setSocialProfileInput('');
    }
  };

  const handleRemoveSocialProfile = (index) => {
    const newProfiles = formData.social_profiles.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      social_profiles: newProfiles
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        social_profiles: formData.social_profiles || []
      };

      if (isEdit) {
        // Update existing vendor
        await vendorService.update(id, submitData);
        navigate('/vendors');
      } else {
        // Create new vendor (admin-only)
        if (user?.role !== 'admin') {
          setError('Only administrators can create vendors from the dashboard');
        } else {
          await vendorService.create(submitData);
          navigate('/vendors');
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message;
      setError(`Failed to ${isEdit ? 'update' : 'create'} vendor: ${errorMessage}`);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const getPageTitle = () => {
    if (isEdit) return 'Edit Vendor';
    return 'Add New Vendor';
  };

  const getPageSubtitle = () => {
    if (isEdit) return `Editing ${formData.name || `vendor #${id}`}`;
    return 'Create a new vendor/company';
  };

  const getSubmitButtonText = () => {
    if (saving) return 'Saving...';
    if (isEdit) return 'Update Vendor';
    return 'Create Vendor';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="px-4">
      <PageHeader
        title={getPageTitle()}
        subtitle={getPageSubtitle()}
      />

      <Row className="mb-3">
        <Col>
          <Button variant="secondary" onClick={() => navigate('/vendors')}>
            <FaArrowLeft className="me-2" />
            Back to Vendors
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Referral flow is handled in the main application (user profile), not the dashboard */}

      <Form onSubmit={handleSubmit}>
        <Row className="g-4">
          <Col lg={8}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <h5 className="fw-semibold mb-4">
                  <FaBuilding className="me-2" />
                  Company Information
                </h5>

                <Form.Group className="mb-3">
                  <Form.Label>Company Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter company name"
                  />
                  <Form.Text className="text-muted">
                    The official name of the company/vendor
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <FaGlobe className="me-1" />
                    Website
                  </Form.Label>
                  <Form.Control
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://example.com"
                  />
                  <Form.Text className="text-muted">
                    Company website URL (optional)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone1"
                    value={formData.phone1 || ''}
                    onChange={handleChange}
                    placeholder="Primary phone number"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Alternate Phone</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone2"
                    value={formData.phone2 || ''}
                    onChange={handleChange}
                    placeholder="Alternate phone number"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>WhatsApp</Form.Label>
                  <Form.Control
                    type="text"
                    name="whatsapp"
                    value={formData.whatsapp || ''}
                    onChange={handleChange}
                    placeholder="WhatsApp number"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Logo URL</Form.Label>
                  <Form.Control
                    type="url"
                    name="logo"
                    value={formData.logo || ''}
                    onChange={handleChange}
                    placeholder="https://.../logo.png"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Cover Image URL</Form.Label>
                  <Form.Control
                    type="url"
                    name="cover"
                    value={formData.cover || ''}
                    onChange={handleChange}
                    placeholder="https://.../cover.jpg"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="location"
                    value={formData.location || ''}
                    onChange={handleChange}
                    placeholder="City, State, Country"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="address"
                    value={formData.address || ''}
                    onChange={handleChange}
                    placeholder="Full postal address"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Social Media Profiles</Form.Label>
                  <div className="d-flex gap-2 mb-2">
                    <Form.Control
                      type="url"
                      placeholder="https://twitter.com/company"
                      value={socialProfileInput}
                      onChange={(e) => setSocialProfileInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSocialProfile())}
                    />
                    <Button
                      type="button"
                      variant="outline-primary"
                      onClick={handleAddSocialProfile}
                      disabled={!socialProfileInput.trim()}
                    >
                      Add
                    </Button>
                  </div>
                  {formData.social_profiles.length > 0 && (
                    <div className="mt-2">
                      {formData.social_profiles.map((profile, index) => (
                        <Badge
                          key={index}
                          bg="secondary"
                          className="me-2 mb-2 p-2"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleRemoveSocialProfile(index)}
                        >
                          {profile}
                          <FaTimes className="ms-2" size={10} />
                        </Badge>
                      ))}
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    Add social media profile URLs (Twitter, Facebook, LinkedIn, etc.)
                  </Form.Text>
                </Form.Group>

                {user?.role === 'admin' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <option value="inactive">Inactive</option>
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Only admins can set vendor status
                    </Form.Text>
                  </Form.Group>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>


            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <h6 className="fw-semibold mb-3">Actions</h6>

                <div className="d-grid gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {getSubmitButtonText()}
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {getSubmitButtonText()}
                      </>
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={() => navigate('/vendors')}
                    disabled={saving}
                  >
                    <FaTimes className="me-2" />
                    Cancel
                  </Button>
                </div>

                {user?.role === 'admin' && (
                  <div className="mt-3 pt-3 border-top">
                    <Form.Check
                      type="checkbox"
                      id="verify_badge"
                      name="verify_badge"
                      label="Set verified badge"
                      checked={!!formData.verify_badge}
                      onChange={(e) => setFormData(prev => ({ ...prev, verify_badge: e.target.checked }))}
                    />
                  </div>
                )}


              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mt-4">
              <Card.Body>
                <h6 className="fw-semibold mb-3">
                  <FaUser className="me-2" />
                  Owner Information
                </h6>
                <hr />

                <div className="text-muted small mb-2">
                  <p>
                    <strong>Company Owner:</strong>{' '}
                    {vendorOwner
                      ? `${vendorOwner.username} (${vendorOwner.email || 'no-email'})`
                      : 'Not assigned'}
                  </p>
                </div>

                {(user?.role === 'admin' || vendorOwner?.id === user?.id) && (
                  <Form.Group className="mb-3">
                    <Form.Label>Owner</Form.Label>

                    {!showOwnerEditor ? (
                      <div className="d-flex gap-2">
                        <Button variant="outline-primary" size="sm" onClick={() => { setShowOwnerEditor(true); setOwnerSearch(''); setOwnerSuggestions([]); }}>
                          Change Owner
                        </Button>
                        <Form.Text className="text-muted align-self-center">Current owner ID: {formData.owner_id || 'none'}</Form.Text>
                      </div>
                    ) : (
                      <>
                        <Form.Control
                          type="text"
                          placeholder="Search users by username or email..."
                          value={ownerSearch}
                          onChange={(e) => setOwnerSearch(e.target.value)}
                        />

                        {ownerSearching && <div className="small text-muted mt-1">Searching...</div>}

                        {ownerSuggestions.length > 0 && (
                          <div className="border rounded mt-1 bg-white" style={{ maxHeight: 200, overflowY: 'auto', zIndex: 1000 }}>
                            {ownerSuggestions.map((u) => (
                              <div
                                key={u.id}
                                role="button"
                                onClick={() => {
                                  setFormData((prev) => ({ ...prev, owner_id: u.id }));
                                  setVendorOwner(u);
                                  setOwnerSearch(u.username + (u.email ? ` (${u.email})` : ''));
                                  setOwnerSuggestions([]);
                                  setShowOwnerEditor(false);
                                }}
                                className="px-2 py-2 hover-bg-light"
                                style={{ cursor: 'pointer' }}
                              >
                                <strong>{u.username}</strong>
                                <div className="small text-muted">{u.email}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="d-flex gap-2 mt-2">
                          <Button variant="primary" size="sm" onClick={() => setShowOwnerEditor(false)}>
                            Done
                          </Button>
                          <Button variant="outline-secondary" size="sm" onClick={() => { setShowOwnerEditor(false); setOwnerSearch(''); setOwnerSuggestions([]); }}>
                            Cancel
                          </Button>
                        </div>
                      </>
                    )}
                  </Form.Group>
                )}

                {/* Referral-specific messaging removed from dashboard */}
              </Card.Body>
            </Card>
            {/* Referral and verify card removed per dashboard policy; verification remains admin-only via vendor list */}
          </Col>
        </Row>
      </Form>
    </Container>
  );
}

export default VendorEditPage;